import {
  isClientInteractionEnabled,
  observeClientInteraction,
  reportClientInteractionError,
  withRelayCommandObserver,
} from '@github-ui/ahp-relay/relay-command-observation'
import type {StateAction} from '@microsoft/agent-host-protocol'
import {
  preparedPromptObservations,
  type EstablishedAhpSessionIdentity,
  type PreparedPrompt,
  type PreparedPromptTransform,
} from './contracts'
import {matchesCommandPayload, snapshotCommandPayload} from './client-command-payload'

export interface ClientPreparationOptions {
  readonly interaction?: {dependencyChanged(dependency: 'attachments', waiting: boolean): void}
}

export type ClientCommandDisposition =
  | 'cancelled'
  | 'reset'
  | 'disposed'
  | 'superseded'
  | 'handoff_unobserved'
  | 'observation_capacity'

export interface ClientCommandObserver {
  sent(identity: EstablishedAhpSessionIdentity, channel: string, action: StateAction, requestId?: string): void
  failed(reason?: 'transport_failed' | 'payload_mismatch' | 'session_mismatch'): void
  unavailable(): void
  waiting(waiting: boolean, dependency?: 'mutation_lane' | 'session' | 'configuration'): void
  bind?(identity: EstablishedAhpSessionIdentity): void
  bindTarget?(identity: EstablishedAhpSessionIdentity, turnId: string): void
  prepared?(source: PreparedPrompt, target: PreparedPrompt, transform?: PreparedPromptTransform): void
  disposition?(reason: ClientCommandDisposition): void
}

let prompts: WeakMap<PreparedPrompt, ClientCommandObserver> | undefined
let preparationIntegrity: WeakMap<PreparedPrompt, boolean> | undefined
let stopObserver: ClientCommandObserver | undefined
let handoffs: Map<string, {id: string; observer: ClientCommandObserver}> | undefined
export const MAX_LOCAL_COMMAND_HANDOFFS = 64

export function retainClientCommandHandoff(key: string, id: string, prompt: PreparedPrompt): void {
  const observer = prompts?.get(prompt)
  if (!observer) return
  observeClientInteraction(() => {
    handoffs ??= new Map()
    const previous = handoffs.get(key)
    if (previous && (previous.id !== id || previous.observer !== observer)) {
      observeClientInteraction(() => previous.observer.disposition?.('superseded'))
    }
    handoffs.set(key, {id, observer})
    if (handoffs.size > MAX_LOCAL_COMMAND_HANDOFFS) {
      const oldest = handoffs.entries().next().value
      if (oldest) {
        handoffs.delete(oldest[0])
        observeClientInteraction(() => oldest[1].observer.disposition?.('observation_capacity'))
        reportClientInteractionError('observation_capacity')
      }
    }
  })
}

export function restoreClientCommandHandoff(key: string, id: string, prompt: PreparedPrompt): void {
  const handoff = handoffs?.get(key)
  if (handoff?.id === id) bindClientCommand(prompt, handoff.observer)
}

export function releaseClientCommandHandoff(key: string, id?: string, reason?: ClientCommandDisposition): void {
  const handoff = handoffs?.get(key)
  if (handoff && (id === undefined || handoff.id === id)) {
    handoffs?.delete(key)
    if (reason) observeClientInteraction(() => handoff.observer.disposition?.(reason))
  }
}

export function releaseClientCommandObserver(observer: ClientCommandObserver): void {
  if (!handoffs) return
  for (const [key, handoff] of handoffs) {
    if (handoff.observer === observer) handoffs.delete(key)
  }
}

export function clientStopObserver(): ClientCommandObserver | undefined {
  return stopObserver
}

export function withClientStopObserver<T>(observer: ClientCommandObserver | undefined, stop: () => T): T {
  if (!observer) return stop()
  const previous = stopObserver
  stopObserver = observer
  try {
    return stop()
  } finally {
    stopObserver = previous
  }
}

export function bindClientCommand(prompt: PreparedPrompt, observer: ClientCommandObserver): void {
  prompts ??= new WeakMap()
  prompts.set(prompt, observer)
  observePromptCopies(prompt)
}

export function clientCommandObserver(prompt: PreparedPrompt): ClientCommandObserver | undefined {
  return prompts?.get(prompt)
}

function observePromptCopies(source: PreparedPrompt) {
  const observer = prompts?.get(source)
  const integrity = preparationIntegrity?.get(source)
  preparedPromptObservations.observe(source, {
    copied(target, transform, verifyPayload) {
      if (observer && verifyPayload) observeClientInteraction(() => observer.prepared?.(source, target, transform))
      if (observer) {
        prompts ??= new WeakMap()
        prompts.set(target, observer)
      }
      if (integrity !== undefined) {
        preparationIntegrity ??= new WeakMap()
        preparationIntegrity.set(target, integrity)
      }
      observePromptCopies(target)
    },
    failed: () => reportClientInteractionError('observer'),
  })
}

export function copyClientCommand<T extends PreparedPrompt>(
  source: PreparedPrompt,
  target: T,
  transform?: PreparedPromptTransform,
): T {
  return preparedPromptObservations.copy(source, target, transform)
}

export function recordClientCommandPreparation<T extends PreparedPrompt>(prompt: T, intact: boolean): T {
  if (isClientInteractionEnabled()) {
    observeClientInteraction(() => {
      preparationIntegrity ??= new WeakMap()
      preparationIntegrity.set(prompt, intact)
      observePromptCopies(prompt)
    })
  }
  return prompt
}

export function clientCommandPreparationIntegrity(prompt: PreparedPrompt): boolean | undefined {
  return preparationIntegrity?.get(prompt)
}

export function observeSessionCommand<T>(
  observer: ClientCommandObserver | undefined,
  identity: EstablishedAhpSessionIdentity,
  dispatch: () => T,
  requestId?: string,
): T {
  if (!observer) return dispatch()
  return withRelayCommandObserver(
    {
      sent: (channel, action) => {
        try {
          observer.sent(identity, channel, action, requestId)
        } finally {
          releaseClientCommandObserver(observer)
        }
      },
      failed: () => {
        try {
          observer.failed()
        } finally {
          releaseClientCommandObserver(observer)
        }
      },
      unavailable: () => {
        try {
          observer.unavailable()
        } finally {
          releaseClientCommandObserver(observer)
        }
      },
    },
    dispatch,
  )
}

export function observeMutationWait<T>(
  observer: ClientCommandObserver | undefined,
  wait: () => Promise<T>,
): Promise<T> {
  if (!observer) return wait()
  observeClientInteraction(() => observer.waiting(true))
  const ended = () => observeClientInteraction(() => observer.waiting(false))
  let result: Promise<T>
  try {
    result = wait()
  } catch (error) {
    ended()
    throw error
  }
  observeClientInteraction(() => {
    // eslint-disable-next-line github/no-then -- Observe the original wait without adding an awaited promise to product timing.
    void result.then(ended, ended)
  })
  return result
}

export function observeQueuedCommandRevision(
  observer: ClientCommandObserver,
  pendingMessageId: string,
  prompt: PreparedPrompt,
  identity: EstablishedAhpSessionIdentity,
) {
  type Sent = Parameters<ClientCommandObserver['sent']>
  const expected = snapshotCommandPayload(prompt)
  const sessionId = identity.sessionId
  const environmentId = identity.environmentId
  let message: Sent | undefined
  let removal: Sent | undefined
  let finished = false
  const fail = (reason: Parameters<ClientCommandObserver['failed']>[0] = 'transport_failed') => {
    if (finished) return
    finished = true
    message = undefined
    removal = undefined
    releaseClientCommandObserver(observer)
    observeClientInteraction(() => observer.failed(reason))
  }
  const complete = () => {
    if (finished || !message || !removal) return
    if (
      message[0].sessionId !== removal[0].sessionId ||
      message[0].environmentId !== removal[0].environmentId ||
      message[1] !== removal[1]
    ) {
      fail('session_mismatch')
      return
    }
    finished = true
    const sent = message
    message = undefined
    removal = undefined
    releaseClientCommandObserver(observer)
    observeClientInteraction(() => observer.sent(...sent))
  }
  const shared = {
    failed: fail,
    unavailable: () => observer.unavailable(),
    waiting: (waiting: boolean) => observer.waiting(waiting),
  }
  return {
    message: {
      ...shared,
      sent: (...args: Sent) => {
        if (finished) return
        if (args[0].sessionId !== sessionId || args[0].environmentId !== environmentId) {
          fail('session_mismatch')
          return
        }
        if (
          !matchesCommandPayload(expected, args[2]) ||
          (args[2].type === 'chat/pendingMessageSet' &&
            (args[2].id !== pendingMessageId || args[2].kind !== 'steering'))
        ) {
          fail('payload_mismatch')
          return
        }
        if (message) return
        message = args
        complete()
      },
    },
    removal: {
      ...shared,
      sent: (...args: Sent) => {
        if (finished) return
        const action = args[2]
        if (
          action.type !== 'chat/pendingMessageRemoved' ||
          action.id !== pendingMessageId ||
          action.kind !== 'queued'
        ) {
          fail('payload_mismatch')
          return
        }
        if (removal) return
        removal = args
        complete()
      },
    },
    fail,
  }
}
